<template>
    <div class="widget-config-entity-tree">
        <RedesignFormLabel
            :label="templateParameterHelpName"
            :info-text="templateParameterHelpDescription"
            class="mb-4"
        />
        <RedesignRadioGroup
            v-model="selectionType"
            class="flex flex-col gap-3"
        >
            <RedesignRadio :label="ENTITY_SELECTION_TYPE.POSITIVE">
                {{ $t('autocomplete.includeTheSelected') }}
            </RedesignRadio>
            <RedesignRadio :label="ENTITY_SELECTION_TYPE.NEGATIVE">
                {{ $t('autocomplete.excludeTheSelected') }}
            </RedesignRadio>
        </RedesignRadioGroup>

        <div
            class="mt-5"
            :class="{ 'opacity-50 pointer-events-none': !selectionType }"
        >
            <!-- Search Input -->
            <div class="entity-tree-search mb-3">
                <el-input
                    v-model="searchQuery"
                    :placeholder="$t('common.search')"
                    prefix-icon="el-icon-search"
                    size="small"
                    clearable
                    @input="handleSearch"
                />
            </div>

            <div
                v-if="isAccountSelection && selectedAccountTags.length"
                class="flex flex-nowrap items-center gap-1 mb-3"
            >
                <div
                    v-for="tag in selectedAccountTags.slice(0, 2)"
                    :key="tag.key"
                    class="inline-flex items-center gap-1 rounded-full bg-gray-100 ps-2 pe-1 py-0.5 max-w-[40%]"
                >
                    <span class="truncate">{{ tag.label }}</span>
                    <RedesignBaseIconButton
                        class="rounded-full text-gray hover:bg-white"
                        icon="X"
                        :size="14"
                        @click="removeSelectedTag(tag.key)"
                    />
                </div>
                <RedesignTooltip v-if="selectedAccountTags.length > 2">
                    <template #tooltip-content>
                        <div class="flex flex-wrap gap-1 max-w-xs max-h-48 overflow-y-auto">
                            <div
                                v-for="tag in selectedAccountTags.slice(2)"
                                :key="tag.key"
                                class="inline-flex items-center gap-1 rounded-full bg-gray-100 ps-2 pe-1 py-0.5 max-w-full"
                            >
                                <span class="truncate">{{ tag.label }}</span>
                                <RedesignBaseIconButton
                                    class="rounded-full text-gray hover:bg-white"
                                    icon="X"
                                    :size="14"
                                    @click="removeSelectedTag(tag.key)"
                                />
                            </div>
                        </div>
                    </template>
                    <div class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 cursor-pointer">
                        +{{ selectedAccountTags.length - 2 }}
                    </div>
                </RedesignTooltip>
            </div>

            <div
                v-if="isLoading"
                class="text-gray-500 text-sm py-4"
            >
                {{ $t('common.loading') }}...
            </div>
            <RedesignBaseTree
                v-else-if="treeData.length"
                ref="treeRef"
                :data="treeData"
                node-key="nodeKey"
                show-checkbox
                highlight-current
                accordion
                :check-strictly="isAccountSelection"
                :default-checked-keys="currentSelection"
                :filter-node-method="filterNode"
                @check="handleTreeCheck"
            >
                <template #node="{ data: nodeData }">
                    <span
                        v-if="nodeData.type === ACCOUNT_NODE_TYPE"
                        class="font-semibold text-primary inline-flex items-center gap-1.5"
                    >
                        <span
                            v-if="parentKeysWithSelection.has(nodeData.nodeKey)"
                            class="w-1.5 h-1.5 rounded-full bg-destructive-actions"
                        />
                        {{ nodeData.label }}
                    </span>
                    <div
                        v-else
                        class="flex gap-1 items-center"
                    >
                        <RedesignBaseIcon
                            v-if="entityIcon"
                            :name="entityIcon"
                            class="w-4 h-4"
                        />
                        <span :title="nodeData.label">{{ nodeData.label }}</span>
                    </div>
                </template>
            </RedesignBaseTree>
            <div
                v-else
                class="text-gray-500 text-sm py-4"
            >
                {{ $t('common.noData') }}
            </div>

            <RedesignWidgetConfigEntityTreeOrder
                v-if="showOrderComponent && selectionType"
                :items="displayItemsForOrder"
                :selection-type="selectionType"
                :entity-icon="entityIcon"
                :is-loading="isLoading"
                @update:order="handleOrderUpdate"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useDebounceFn, useVModel } from '@vueuse/core'
import { Input as ElInput } from 'element-ui'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadio from '@/components/RedesignBaseComponents/Inputs/RedesignRadio.vue'
import RedesignBaseTree from '@/components/RedesignBaseComponents/Inputs/RedesignBaseTree.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import RedesignWidgetConfigEntityTreeOrder from './RedesignWidgetConfigEntityTreeOrder.vue'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName
} from '@/components/RedesignComponents/Widgets/widget.helper'
import {
    ENTITY_SELECTION_TYPE,
    type EntitySelectionType,
    isEntitySelectionType,
    resolveEntitySelectionType
} from '@/enum/entitySelection'
import { ACCOUNT_NODE_TYPE, buildEntityHierarchyTree, findNodeByKey, type EntityTreeNode } from '@/helpers/entityHierarchyTree'
import { getOptionsList, getTemplateConfig } from '@/helpers/entitiesList'
import { getEntityList } from '@/composables/globalData/useEntitiesData'
import type { WidgetConfig } from '@/types/dashboard'
import { ENTITIES_CONFIG, EntityKey, EntityData } from '@/enum/etitiesConfig'
import { AccountEntity } from '@/types/entities'

/* Types */
type KeysValue = (string | number)[]
type SelectionMap = Record<EntitySelectionType, KeysValue>

/* Props & Emits */
const props = withDefaults(defineProps<{
    value: WidgetConfig
    orderedList?: (string | number)[]
    showOrderComponent?: boolean
    allowEmptySelection?: boolean
}>(), {
    allowEmptySelection: false
})

const emit = defineEmits<{
    (e: 'update:value', value: WidgetConfig): void
    (e: 'update:sortingParams', params: { defaultList: (string | number)[], idKey: string }): void
}>()

/* State */
const modelValue = useVModel(props, 'value', emit)
const treeRef = shallowRef<InstanceType<typeof RedesignBaseTree> | null>(null)
const selectionType = shallowRef<EntitySelectionType | null>(
    props.allowEmptySelection ? null : ENTITY_SELECTION_TYPE.NEGATIVE
)

const localSelectionMap = shallowRef<SelectionMap>({
    [ENTITY_SELECTION_TYPE.POSITIVE]: [],
    [ENTITY_SELECTION_TYPE.NEGATIVE]: [],
})

const searchQuery = ref('')
const entities = shallowRef<EntityData[]>([])
const isLoading = ref(true)
const treeData = shallowRef<EntityTreeNode[]>([])
const accountsCache = shallowRef<AccountEntity[]>([])

// Performance: Cache account keys Set for O(1) filtering
const accountKeysSet = shallowRef<Set<string | number>>(new Set())

// Flag to prevent watch from interfering with drag and drop
const isDragging = ref(false)

// Track if we're selecting accounts themselves (not entities under accounts)
const isAccountSelection = shallowRef(false)

let isInitialized = false
let searchInputEl: HTMLInputElement | null = null

/* Computed */
const templateConfig = computed(() => {
    return getTemplateConfig(props.value.ParameterName ?? '')
})

const entityKey = computed(() => {
    return templateConfig.value?.EntitiesListKey ?? null
})

const entityConfig = computed(() => {
    const key = entityKey.value
    return isEntityKey(key)
        ? ENTITIES_CONFIG[key]
        : null
})

const entityIcon = computed(() => {
    return entityConfig.value?.icon ?? 'Circle'
})

const templateParameterHelpName = computed(() => {
    return getTemplateParameterHelpName(props.value)
})

const templateParameterHelpDescription = computed(() => {
    return getTemplateParameterHelpDescription(props.value)
})

const currentSelection = computed(() => {
    if (!selectionType.value) return []

    const selection = localSelectionMap.value[selectionType.value] ?? []

    // Convert numeric account IDs back to node keys for tree display
    if (isAccountSelection.value) {
        return convertToAccountNodeKeys(selection)
    }

    return selection
})

const selectedAccountTags = computed(() => {
    if (!isAccountSelection.value) return []
    return currentSelection.value.flatMap((key) => {
        const node = findNodeByKey(treeData.value, key)
        return node ? [{ key, label: node.label }] : []
    })
})

/** Parent keys that have at least one checked descendant (for check indicator) */
const parentKeysWithSelection = computed(() => {
    const keys = new Set<string | number>()
    if (!isAccountSelection.value || !currentSelection.value.length) return keys

    const selected = new Set(currentSelection.value)
    function walk (nodes: EntityTreeNode[]): boolean {
        let hasSelectedBelow = false
        for (const node of nodes) {
            const hasSelectedInChildren = node.children?.length ? walk(node.children) : false
            if (hasSelectedInChildren) keys.add(node.nodeKey)
            if (selected.has(node.nodeKey) || hasSelectedInChildren) hasSelectedBelow = true
        }
        return hasSelectedBelow
    }
    walk(treeData.value)
    return keys
})

function collectAllEntities (): Array<{ key: string | number, label: string }> {
    if (!entities.value.length || !entityConfig.value) {
        return []
    }

    const idKey = entityConfig.value.idKey
    const labelKey = entityConfig.value.labelKey

    return entities.value.map(entity => {
        const id = entity[idKey]
        const label = entity[labelKey] ?? ''
        const key = isAccountSelection.value
            ? accountIdToNodeKey(+id)
            : id

        return {
            key,
            label: String(label)
        }
    })
}

/* Order-related helpers */
// Helper: Get savedOrder array safely
const savedOrder = computed(() => {
    return Array.isArray(props.orderedList) ? props.orderedList : []
})

// Helper: Convert nodeKey to entityId
function nodeKeyToEntityId (nodeKey: string | number): string | number | null {
    return isAccountSelection.value
        ? extractAccountId(nodeKey)
        : nodeKey
}

// Helper: Sort items by savedOrder
function sortItemsByOrder<T extends { key: string | number }> (
    items: T[],
    order: (string | number)[]
): T[] {
    if (!order.length) return items

    const orderMap = new Map(order.map((id: string | number, index: number) => [ id, index ]))

    const itemsWithOrder = items.map(item => {
        const entityId = nodeKeyToEntityId(item.key)
        const order = entityId !== null ? (orderMap.get(entityId) ?? Infinity) : Infinity
        return {
            item,
            order
        }
    })

    itemsWithOrder.sort((a, b) => a.order - b.order)

    return itemsWithOrder.map(({ item }) => item)
}


const displayItemsForOrder = computed(() => {
    // If component is not shown - skip computation
    if (props.showOrderComponent === false || !selectionType.value) {
        return []
    }

    if (selectionType.value === ENTITY_SELECTION_TYPE.POSITIVE) {
        // Include mode: show selected items (or all if nothing selected)
        if (currentSelection.value.length === 0) {
            return collectAllEntities()
        }

        // Convert currentSelection to items
        const items: Array<{ key: string | number, label: string }> = []
        for (const nodeKey of currentSelection.value) {
            const node = findNodeByKey(treeData.value, nodeKey)
            if (node) {
                items.push({
                    key: nodeKey,
                    label: node.label
                })
            }
        }
        return sortItemsByOrder(items, savedOrder.value)
    } else {
        // Exclude mode: show unselected items
        const selectedKeys = localSelectionMap.value[ENTITY_SELECTION_TYPE.NEGATIVE] ?? []
        const selectedKeysForLookup = isAccountSelection.value
            ? convertToAccountNodeKeys(selectedKeys)
            : selectedKeys

        const selectedKeysSet = new Set(selectedKeysForLookup)
        const allEntities = collectAllEntities()

        // Filter out selected items
        const unselectedNodes = allEntities.filter(item => {
            if (isAccountSelection.value) {
                return !selectedKeysSet.has(item.key)
            } else {
                return !accountKeysSet.value.has(item.key) && !selectedKeysSet.has(item.key)
            }
        })
        return sortItemsByOrder(unselectedNodes, savedOrder.value)
    }
})

/**
 * Lookup map for paste functionality
 * Maps both entity names (labels) and entity IDs (values) to their node keys
 * This enables pasting comma-separated names or IDs
 */
const entitiesLookupMap = computed(() => {
    if (!entities.value.length || !entityConfig.value) {
        return new Map<string, string | number>()
    }

    const lookupMap = new Map<string, string | number>()
    const idKey = entityConfig.value.idKey
    const nameKey = entityConfig.value.labelKey

    for (const entity of entities.value) {
        const id = entity[idKey]
        const name = entity[nameKey]

        if (id) {
            // For account selection, use account node key format
            const nodeKey = isAccountSelection.value ? accountIdToNodeKey(+id) : id

            lookupMap.set(String(id), nodeKey)

            if (name) {
                lookupMap.set(name.toLowerCase().trim(), nodeKey)
            }
        }
    }

    return lookupMap
})

/* Methods */
/**
 * Performance: Build tree and cache all derived data
 * Only called when entities actually change
 */
function buildAndCacheTree () {
    if (!entityKey.value || !entityConfig.value || !entities.value.length) {
        treeData.value = []
        accountKeysSet.value = new Set()
        isAccountSelection.value = false
        return
    }

    // Use cached accounts
    if (!accountsCache.value.length) {
        accountsCache.value = getEntityList('Accounts')
    }

    if (!accountsCache.value.length) {
        treeData.value = []
        accountKeysSet.value = new Set()
        isAccountSelection.value = false
        return
    }
    const { tree } = buildEntityHierarchyTree(accountsCache.value, entities.value, entityConfig.value)

    treeData.value = tree

    // Check if we're selecting accounts themselves (not entities under accounts)
    isAccountSelection.value = entityConfig.value.idKey === ENTITIES_CONFIG.Accounts.idKey

    const accKeys = new Set<string | number>()

    function collectKeys (nodes: EntityTreeNode[]) {
        for (const node of nodes) {
            if (node.type === ACCOUNT_NODE_TYPE) {
                accKeys.add(node.nodeKey)
                if (node.children?.length) {
                    collectKeys(node.children)
                }
            }
        }
    }

    collectKeys(tree)
    accountKeysSet.value = accKeys
}

/**
 * Load entities using the centralized getOptionsList helper
 * This ensures consistent filtering based on selectedAccountEntitiesData
 */
async function getData () {
    if (!props.value.ParameterName) return
    isLoading.value = true
    try {
        entities.value = await getOptionsList(props.value.ParameterName)
        buildAndCacheTree()
    } finally {
        isLoading.value = false
    }
}

function filterNode (value: string, data: EntityTreeNode): boolean {
    if (!value) return true
    return data.label.toLowerCase().includes(value.toLowerCase())
}

function updateModel (value: SelectionMap, currentSelectionType: EntitySelectionType = selectionType.value) {
    modelValue.value = {
        ...modelValue.value,
        WidgetParameterValueJson: {
            ...modelValue.value.WidgetParameterValueJson,
            [ENTITY_SELECTION_TYPE.POSITIVE]: value[ENTITY_SELECTION_TYPE.POSITIVE],
            [ENTITY_SELECTION_TYPE.NEGATIVE]: value[ENTITY_SELECTION_TYPE.NEGATIVE],
            EntityTypeSelection: currentSelectionType,
        },
    }
}

function buildSelectionModel (type: EntitySelectionType, keys: KeysValue): SelectionMap {
    return {
        [ENTITY_SELECTION_TYPE.POSITIVE]: type === ENTITY_SELECTION_TYPE.POSITIVE ? keys : [],
        [ENTITY_SELECTION_TYPE.NEGATIVE]: type === ENTITY_SELECTION_TYPE.NEGATIVE ? keys : [],
    }
}

function initFromModel () {
    const json = modelValue.value?.WidgetParameterValueJson ?? {}
    const positiveData = json[ENTITY_SELECTION_TYPE.POSITIVE]
    const negativeData = json[ENTITY_SELECTION_TYPE.NEGATIVE]

    const includeList: KeysValue = Array.isArray(positiveData) ? positiveData : []
    const excludeList: KeysValue = Array.isArray(negativeData) ? negativeData : []

    localSelectionMap.value = {
        [ENTITY_SELECTION_TYPE.POSITIVE]: includeList,
        [ENTITY_SELECTION_TYPE.NEGATIVE]: excludeList,
    }

    selectionType.value = props.allowEmptySelection && !isEntitySelectionType(json.EntityTypeSelection)
        ? null
        : resolveEntitySelectionType(
            json.EntityTypeSelection,
            includeList,
            excludeList
        )

    nextTick(() => {
        isInitialized = true
    })
}
/**
 * Extract numeric account ID from node key
 * Converts "account-123" → 123
 */
function extractAccountId (nodeKey: string | number): number | null {
    if (typeof nodeKey === 'number') return nodeKey
    if (nodeKey.startsWith('account-')) {
        const id = parseInt(nodeKey.replace('account-', ''), 10)
        return isNaN(id) ? null : id
    }
    return null
}

/**
 * Convert numeric account ID to node key
 * Converts 123 → "account-123"
 */
function accountIdToNodeKey (accountId: number): string {
    return `account-${accountId}`
}

/**
 * Convert account node keys to numeric IDs
 * Just extracts IDs, doesn't recursively collect (tree handles hierarchy)
 */
function convertToAccountIds (keys: (string | number)[]) {
    const accountIds: number[] = []

    for (const key of keys) {
        const id = extractAccountId(key)
        if (id !== null) {
            accountIds.push(id)
        }
    }

    return accountIds
}

/**
 * Convert numeric account IDs back to node keys for tree
 */
function convertToAccountNodeKeys (ids: (string | number)[]) {
    return ids.map(id => {
        if (typeof id === 'number') {
            return accountIdToNodeKey(id)
        }
        // Already in correct format
        return id
    })
}

function applySelectionKeys (entityKeys: (string | number)[]) {
    const currentType = selectionType.value
    if (!currentType) return

    // CRITICAL: When user selects items in current mode, CLEAR the other mode
    // This ensures only one mode has selections at a time
    localSelectionMap.value = {
        [ENTITY_SELECTION_TYPE.POSITIVE]: currentType === ENTITY_SELECTION_TYPE.POSITIVE ? entityKeys : [],
        [ENTITY_SELECTION_TYPE.NEGATIVE]: currentType === ENTITY_SELECTION_TYPE.NEGATIVE ? entityKeys : [],
    }
    updateModel(buildSelectionModel(currentType, entityKeys))

    nextTick(() => {
        if (!isDragging.value && displayItemsForOrder.value.length > 0) {
            updateOrderedListFromDisplayItems(displayItemsForOrder.value)
        }
    })
}

function handleTreeCheck (
    _data: unknown,
    treeCheckedState: {
        checkedNodes: unknown[]
        checkedKeys: (string | number)[]
        halfCheckedNodes: unknown[]
        halfCheckedKeys: (string | number)[]
    }
) {
    if (!selectionType.value) return

    const entityKeys = isAccountSelection.value
        ? convertToAccountIds(treeCheckedState.checkedKeys)
        : treeCheckedState.checkedKeys.filter((key) => !accountKeysSet.value.has(key))

    applySelectionKeys(entityKeys)
}

function removeSelectedTag (nodeKey: string | number) {
    const accountId = extractAccountId(nodeKey)
    if (accountId === null || !selectionType.value) return

    const entityKeys = (localSelectionMap.value[selectionType.value] ?? [])
        .filter((id) => id !== accountId)

    applySelectionKeys(entityKeys)
    treeRef.value?.setCheckedKeys(convertToAccountNodeKeys(entityKeys), false)
}

/**
 * Performance: Debounced search to avoid filtering on every keystroke
 */
const debouncedFilter = useDebounceFn(() => {
    treeRef.value?.filter(searchQuery.value)
}, 300)

function handleSearch () {
    debouncedFilter()
}

function isEntityKey (key: unknown): key is EntityKey {
    return typeof key === 'string' && key in ENTITIES_CONFIG
}

function updateOrderedListFromDisplayItems (items: Array<{ key: string | number, label: string }>) {
    if (props.showOrderComponent === false || items.length === 0 || isDragging.value) {
        return
    }

    const entityIds = items
        .map(item => nodeKeyToEntityId(item.key))
        .filter((id): id is string | number => id !== null)

    if (entityConfig.value?.idKey) {
        emit('update:sortingParams', {
            defaultList: entityIds,
            idKey: String(entityConfig.value.idKey)
        })
    }
}

function handleOrderUpdate (orderedNodeKeys: (string | number)[]) {
    if (props.showOrderComponent === false) {
        return
    }

    isDragging.value = true

    const orderedEntityIds = isAccountSelection.value
        ? convertToAccountIds(orderedNodeKeys)
        : orderedNodeKeys

    if (entityConfig.value?.idKey) {
        emit('update:sortingParams', {
            defaultList: orderedEntityIds,
            idKey: String(entityConfig.value.idKey)
        })
    }

    nextTick(() => {
        isDragging.value = false
    })
}

/* Watchers */
watch(
    selectionType,
    (newType, oldType) => {
        if (!isInitialized || newType === oldType || !newType) return
        const currentKeys = (treeRef.value?.getCheckedKeys(true) ?? []) as (string | number)[]
        let entityKeys
        if (isAccountSelection.value) {
            // ACCOUNT MODE: Convert account keys to numeric IDs
            entityKeys = convertToAccountIds(currentKeys)
        } else {
            // ENTITY MODE: Filter out account keys
            entityKeys = currentKeys.filter((key) => !accountKeysSet.value.has(key))
        }
        if (oldType) {
            localSelectionMap.value = {
                [ENTITY_SELECTION_TYPE.POSITIVE]: oldType === ENTITY_SELECTION_TYPE.POSITIVE
                    ? entityKeys
                    : localSelectionMap.value[ENTITY_SELECTION_TYPE.POSITIVE],
                [ENTITY_SELECTION_TYPE.NEGATIVE]: oldType === ENTITY_SELECTION_TYPE.NEGATIVE
                    ? entityKeys
                    : localSelectionMap.value[ENTITY_SELECTION_TYPE.NEGATIVE],
            }
        }

        const newTypeSelection = localSelectionMap.value[newType] ?? []
        updateModel(buildSelectionModel(newType, newTypeSelection))

        let keysForTree
        if (isAccountSelection.value) {
            // Convert numeric IDs back to "account-XXX" format for tree
            keysForTree = convertToAccountNodeKeys(newTypeSelection)
        } else {
            // Entity keys are already in correct format
            keysForTree = newTypeSelection
        }

        nextTick(() => {
            if (treeRef.value) {
                // Clear all checked nodes first
                treeRef.value.setCheckedKeys([], false)
                // Then set the new type's selection
                nextTick(() => {
                    treeRef.value?.setCheckedKeys(keysForTree, false)

                    if (!isDragging.value && displayItemsForOrder.value.length > 0) {
                        updateOrderedListFromDisplayItems(displayItemsForOrder.value)
                    }
                })
            }
        })
    }
)

/**
 * Handle paste event on search input
 * Parses comma-separated values (names or IDs) and adds matching entities to selection
 */
function onPaste (e: ClipboardEvent) {
    e.preventDefault()
    const pastedText = e.clipboardData?.getData('text')?.trim()
    if (!pastedText) return

    // Split by comma and trim each item
    const pastedItems = pastedText.split(',').map(item => item.trim()).filter(Boolean)
    if (!pastedItems.length) return

    const matchedNodeKeys: (string | number)[] = []

    for (const item of pastedItems) {
        // Try to find by exact ID first
        let nodeKey = entitiesLookupMap.value.get(item)

        // If not found, try case-insensitive name lookup
        if (nodeKey === undefined) {
            nodeKey = entitiesLookupMap.value.get(item.toLowerCase())
        }

        if (nodeKey !== undefined) {
            matchedNodeKeys.push(nodeKey)
        }
    }

    if (!matchedNodeKeys.length) return

    const currentType = selectionType.value
    if (!currentType) return

    // Get current selection and merge with pasted items (avoid duplicates)
    const currentKeys = localSelectionMap.value[currentType] ?? []
    const currentNodeKeys = isAccountSelection.value
        ? convertToAccountNodeKeys(currentKeys)
        : currentKeys

    const mergedKeys = [ ...new Set([ ...currentNodeKeys, ...matchedNodeKeys ]) ]

    // Convert back to entity keys for storage
    let entityKeys: (string | number)[]
    if (isAccountSelection.value) {
        entityKeys = convertToAccountIds(mergedKeys)
    } else {
        entityKeys = mergedKeys.filter((key) => !accountKeysSet.value.has(key))
    }

    // Update local state and model
    localSelectionMap.value = {
        [ENTITY_SELECTION_TYPE.POSITIVE]: currentType === ENTITY_SELECTION_TYPE.POSITIVE ? entityKeys : [],
        [ENTITY_SELECTION_TYPE.NEGATIVE]: currentType === ENTITY_SELECTION_TYPE.NEGATIVE ? entityKeys : [],
    }

    updateModel(buildSelectionModel(currentType, entityKeys))

    // Update tree checkboxes
    nextTick(() => {
        if (treeRef.value) {
            const keysForTree = isAccountSelection.value
                ? convertToAccountNodeKeys(entityKeys)
                : entityKeys
            treeRef.value.setCheckedKeys(keysForTree, false)
        }
    })
}

async function initData () {
    await getData()
    initFromModel()

    // Set up paste event listener on search input
    nextTick(() => {
        const searchContainer = document.querySelector('.entity-tree-search')
        if (searchContainer) {
            searchInputEl = searchContainer.querySelector('input')
            if (searchInputEl) {
                searchInputEl.addEventListener('paste', onPaste)
            }
        }
    })
}

onMounted(initData)

onBeforeUnmount(() => {
    // Clean up paste event listener
    if (searchInputEl) {
        searchInputEl.removeEventListener('paste', onPaste)
        searchInputEl = null
    }
})
</script>

<style lang="scss" scoped>
.widget-config-entity-tree {
    .entity-tree-search {
        :deep(.el-input__inner) {
            @apply bg-white;
        }
    }
}
</style>
